import React, { useRef, useEffect, useState, useImperativeHandle, forwardRef } from "react";
import { Button } from "@/components/ui/button";
import { Eraser } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

/** Lightweight touch/mouse signature pad. Exposes getDataURL() and isEmpty(). */
const SignaturePad = forwardRef(function SignaturePad({ height = 160 }, ref) {
  const { t } = useLang();
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const dirty = useRef(false);
  const [empty, setEmpty] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    const resize = () => {
      const ratio = window.devicePixelRatio || 1;
      const w = canvas.clientWidth;
      canvas.width = w * ratio;
      canvas.height = height * ratio;
      const ctx = canvas.getContext("2d");
      ctx.scale(ratio, ratio);
      ctx.lineWidth = 2.2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = "#0F1226";
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [height]);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const ctx = canvasRef.current.getContext("2d"); const { x, y } = pos(e); ctx.beginPath(); ctx.moveTo(x, y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = canvasRef.current.getContext("2d"); const { x, y } = pos(e); ctx.lineTo(x, y); ctx.stroke(); dirty.current = true; if (empty) setEmpty(false); };
  const end = () => { drawing.current = false; };

  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    dirty.current = false; setEmpty(true);
  };

  useImperativeHandle(ref, () => ({
    isEmpty: () => !dirty.current,
    getDataURL: () => canvasRef.current.toDataURL("image/png"),
    clear,
  }));

  return (
    <div>
      <canvas
        ref={canvasRef}
        style={{ height, touchAction: "none" }}
        className="w-full rounded-xl border-2 border-dashed border-findo-indigo/30 bg-white cursor-crosshair"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        data-testid="signature-canvas"
      />
      <div className="flex items-center justify-between mt-1.5">
        <span className="text-[11px] text-slate-400">{empty ? t("hc.signInTheBoxAbove") : t("hc.signatureSaved")}</span>
        <Button type="button" variant="ghost" size="sm" onClick={clear} className="h-7 text-xs text-slate-500" data-testid="signature-clear-btn">
          <Eraser size={13} className="mr-1" /> {t("hc.clear")}
        </Button>
      </div>
    </div>
  );
});

export default SignaturePad;
